import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useInventory, useDeleteInventory } from '../../hooks/useInventory';
import { useAuthStore } from '../../stores/auth.store';
import DataTable from '../../components/common/DataTable';
import SearchInput from '../../components/common/SearchInput';
import { formatCurrency } from '../../lib/currency';
import type { Inventory } from '@motra/shared';

export default function InventoryPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const shop = useAuthStore((s) => s.shop);
  const currSym = shop?.curr_sym ?? '$';
  const account = useAuthStore((s) => s.account);

  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');

  const { data: items = [], isLoading } = useInventory(undefined, category || undefined);
  const deleteInventory = useDeleteInventory();

  // Parse categories from account
  let categories: string[] = [];
  try {
    categories = account?.inv_type ? JSON.parse(account.inv_type) : [];
  } catch { /* empty */ }

  // Client-side search filter
  const filtered = items.filter((item: Inventory) => {
    const q = search.toLowerCase();
    return (
      !search ||
      item.name?.toLowerCase().includes(q) ||
      item.barcode?.includes(q) ||
      item.description?.toLowerCase().includes(q)
    );
  });

  const columns = [
    { header: t('name', 'Name'), accessor: 'name' as const },
    { header: t('category', 'Category'), accessor: 'category' as const },
    { header: t('barcode', 'Barcode'), accessor: 'barcode' as const },
    {
      header: t('price', 'Price'),
      accessor: 'price' as const,
      render: (val: unknown) => formatCurrency(Number(val) || 0, currSym),
    },
    {
      header: 'Cost',
      accessor: 'cost' as const,
      render: (val: unknown) => formatCurrency(Number(val) || 0, currSym),
    },
    {
      header: t('quantity', 'Qty'),
      accessor: 'qty' as const,
      render: (val: unknown) => (
        <span className={Number(val) === 0 ? 'text-danger' : ''}>{String(val ?? 0)}</span>
      ),
    },
    {
      header: t('status', 'Status'),
      accessor: 'status' as const,
      render: (val: unknown) => (
        <span className={`badge badge--${val === 'active' ? 'success' : 'neutral'}`}>
          {String(val ?? 'active')}
        </span>
      ),
    },
    {
      header: 'Actions',
      accessor: 'id' as const,
      render: (_val: unknown, row: Inventory) => (
        <div className="action-btns">
          <button
            className="btn btn--sm btn--outline"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/inventory/${row.id}`);
            }}
          >
            {t('edit', 'Edit')}
          </button>
          <button
            className="btn btn--sm btn--danger"
            onClick={(e) => {
              e.stopPropagation();
              if (confirm('Delete this item?')) {
                deleteInventory.mutate(row.id);
              }
            }}
          >
            {t('delete', 'Delete')}
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">{t('inventory', 'Inventory')}</h1>
        <button
          className="btn btn--primary"
          onClick={() => navigate('/inventory/new')}
        >
          + {t('add', 'Add Item')}
        </button>
      </div>

      <div className="page-filters">
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search name, barcode..."
        />
        <select
          className="form-select"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
        >
          <option value="">{t('all', 'All Categories')}</option>
          {categories.map((cat) => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>
      </div>

      <DataTable
        columns={columns}
        data={filtered}
        loading={isLoading}
        onRowClick={(row) => navigate(`/inventory/${row.id}`)}
        rowKey={(row) => row.id}
        emptyMessage="No inventory items found"
      />
    </div>
  );
}
